import { Crown } from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';

// Each metric: label, value getter, formatter, and whether lower is better.
const metrics = (period) => {
  const rev = { today: 'revenue_today', '7d': 'revenue_7d', '30d': 'revenue_30d' }[period];
  const sales = { today: 'sales_today', '7d': 'sales_7d', '30d': 'sales_30d' }[period];
  const sellers = { today: 'sellers_today', '7d': 'sellers_7d', '30d': 'sellers_30d' }[period];
  return [
    { key: 'revenue', label: 'Revenue', get: b => b[rev], fmt: fmtCurrency, group: 'Sales' },
    { key: 'sales', label: 'Transactions', get: b => b[sales], group: 'Sales' },
    { key: 'avg', label: 'Average sale', get: b => (b[sales] ? b[rev] / b[sales] : 0), fmt: fmtCurrency, group: 'Sales' },
    { key: 'staff', label: 'Staff on roster', get: b => b.staff_count, group: 'Staff' },
    { key: 'sellers', label: 'Staff who sold', get: b => b[sellers], group: 'Staff' },
    { key: 'per_seller', label: 'Revenue per seller', get: b => (b[sellers] ? b[rev] / b[sellers] : 0), fmt: fmtCurrency, group: 'Staff' },
    { key: 'products', label: 'Products', get: b => b.total_products, group: 'Stock' },
    { key: 'stock_value', label: 'Stock value (retail)', get: b => b.stock_value, fmt: fmtCurrency, group: 'Stock' },
    { key: 'low', label: 'Low stock', get: b => b.low_stock, lowerBetter: true, group: 'Stock' },
    { key: 'out', label: 'Out of stock', get: b => b.out_of_stock, lowerBetter: true, group: 'Stock' },
  ];
};

const bestIndex = (branches, m) => {
  const pool = m.group === 'Stock' ? branches.map((b, i) => (b.total_products > 0 ? i : -1)).filter(i => i >= 0) : branches.map((_, i) => i);
  if (pool.length < 2) return -1;
  const vals = pool.map(i => m.get(branches[i]));
  const target = m.lowerBetter ? Math.min(...vals) : Math.max(...vals);
  const hits = vals.filter(v => v === target).length;
  if (hits !== 1 || (!m.lowerBetter && target <= 0)) return -1;
  return pool[vals.indexOf(target)];
};

export const HeadOfficeCompare = ({ branches, period }) => {
  if (branches.length < 2) return null;
  const rows = metrics(period);
  return (
    <div className="bg-white border border-slate-100/80 rounded-[1.25rem] shadow-[0_4px_24px_-8px_rgba(0,0,0,0.08)] p-6" data-testid="head-office-compare">
      <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 pb-4 mb-2">
        <h3 className="text-sm font-semibold text-slate-800">Side by side</h3>
        <p className="text-xs text-slate-500 flex items-center gap-1"><Crown className="h-3.5 w-3.5 text-amber-500" /> marks the leading branch for each line</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 180 + branches.length * 150 }}>
          <thead>
            <tr>
              <th className="sticky left-0 bg-white py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Metric</th>
              {branches.map(b => (
                <th key={b.tenant_id} className="py-3 px-3 text-right text-xs font-semibold text-slate-700" data-testid={`compare-col-${b.tenant_id}`}>{b.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => {
              const best = bestIndex(branches, m);
              const newGroup = i === 0 || rows[i - 1].group !== m.group;
              return (
                <tr key={m.key} className={newGroup ? 'border-t border-slate-200' : 'border-t border-slate-50'} data-testid={`compare-row-${m.key}`}>
                  <td className="sticky left-0 bg-white py-2.5 pr-4 text-slate-600">
                    {newGroup && <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">{m.group}</span>}
                    {m.label}
                  </td>
                  {branches.map((b, j) => (
                    <td key={b.tenant_id} className={`py-2.5 px-3 text-right tabular-nums ${j === best ? 'font-semibold text-emerald-700' : 'text-slate-800'}`}
                      data-testid={`compare-${m.key}-${b.tenant_id}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {j === best && <Crown className="h-3.5 w-3.5 text-amber-500" data-testid={`compare-leader-${m.key}`} />}
                        {m.fmt ? m.fmt(m.get(b)) : Math.round(m.get(b) * 100) / 100}
                      </span>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
